/**
 * feat-299 — "only one field is open at a time" (spec §3, NOTES §3).
 *
 * Opening a second inline field commits the first through the SAME path as a
 * blur. In a real browser the first textarea usually blurs on its own when the
 * reviewer clicks elsewhere; this coordinator is what makes the rule hold when
 * it does not (keyboard activation of another run, a mark-correct click whose
 * button never takes focus, and jsdom, where `click` never blurs anything).
 *
 * A commit is idempotent on the field's side (it only acts while the field is
 * still open), so a blur followed by a coordinator commit cannot double-save.
 *
 * Also the `EditLine` wrapper: while one of its runs is open, the line becomes
 * `flex items-start gap-1.5` with the prefix (`Q2.`, `B)`, `🎉`, …) pinned as
 * `shrink-0`, so the editor takes the rest of the line and its text starts
 * where it sat at rest (NOTES §3 "Layout: minimal reflow").
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export interface EditCoordinator {
  /** A field opened. Commits whichever other field is open first. */
  claim: (token: object, commit: () => void) => void;
  /** A field closed (saved, cancelled, or unmounted). */
  release: (token: object) => void;
  /** Commit the open field, if any — e.g. before a mark-correct save. */
  commitOpen: () => void;
}

const EditCoordinatorContext = createContext<EditCoordinator | null>(null);

export const EditCoordinatorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const openRef = useRef<{ token: object; commit: () => void } | null>(null);
  const value = useMemo<EditCoordinator>(
    () => ({
      claim: (token, commit) => {
        const prev = openRef.current;
        openRef.current = { token, commit };
        if (prev && prev.token !== token) prev.commit();
      },
      release: (token) => {
        if (openRef.current?.token === token) openRef.current = null;
      },
      commitOpen: () => {
        const prev = openRef.current;
        if (prev) prev.commit();
      },
    }),
    [],
  );
  return <EditCoordinatorContext.Provider value={value}>{children}</EditCoordinatorContext.Provider>;
};

/** `null` outside a provider: a lone primitive (tests, future surfaces) still works. */
export function useEditCoordinator(): EditCoordinator | null {
  return useContext(EditCoordinatorContext);
}

// ------------------------------------------------------------ EditLine

const EditLineContext = createContext<((open: boolean) => void) | null>(null);

export function useEditLineReporter(): ((open: boolean) => void) | null {
  return useContext(EditLineContext);
}

/**
 * One line holding a prefix and an editable run. At rest it renders exactly
 * `{prefix} {children}` inside `className`; while a run inside it is open it
 * reflows to a flex row (NOTES §3).
 */
export const EditLine: React.FC<{
  as?: 'div' | 'span' | 'h3' | 'h4' | 'li';
  className?: string;
  prefix: React.ReactNode;
  children: React.ReactNode;
  /** Passed through for the Lesson/Drill headings (TOC focus target). */
  headingProps?: Record<string, unknown>;
}> = ({ as = 'div', className = '', prefix, children, headingProps }) => {
  const [open, setOpen] = useState(false);
  const report = useCallback((o: boolean) => setOpen(o), []);
  const Tag = as as React.ElementType;
  return (
    <EditLineContext.Provider value={report}>
      {/* The CHILDREN slot (third) keeps its type and position in both states,
          so the run inside is never remounted (it holds the open editor's
          state). At rest the prefix is bare text in the line, exactly as before
          feat-299; only while open is it wrapped to pin it `shrink-0`. */}
      <Tag className={open ? `${className} flex items-start gap-1.5` : className} {...headingProps}>
        {open ? <span className="shrink-0 py-0.5">{prefix}</span> : <>{prefix}</>}
        {open ? null : ' '}
        <span className={open ? 'flex-1 min-w-0' : undefined}>{children}</span>
      </Tag>
    </EditLineContext.Provider>
  );
};
